import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages } from '../../contexts/SiteContext';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { productsWithIngredient } from './shopRules';

/** Zoveel producten naast een stof. */
const SHOWN = 3;

/**
 * "Wat er écht in zit" (Paginateksten › Webshop › Ingrediënten). Links de werkzame stoffen als
 * een grote, rustige lijst; kiezen (klik, tik, toetsenbord of aanwijzen met de muis) toont
 * rechts wat de stof doet en in welke producten hij zit, geteld uit het assortiment.
 * Een stof zonder product valt vanzelf weg; zonder stoffen geen blok.
 */
export function IngredientIndex() {
  const pages = usePages();
  const {
    products
  } = useCatalog();
  const reduce = useReducedMotion();
  const panelId = useId();
  const copy = pages.shop.ingredients;
  const groups = copy.items.map((g, i) => ({
    ...g,
    key: `${i}-${g.name}`,
    products: productsWithIngredient(products, g.match)
  })).filter(g => g.name && g.products.length > 0);
  const [activeKey, setActiveKey] = useState(groups[0]?.key);
  const active = groups.find(g => g.key === activeKey) ?? groups[0];
  if (!active) return null;
  return <Section tone="shell" labelledBy="ingredients-title" contained={false} data-id="element-6749" data-mp="3gblew7j64gdj">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6750" data-mp="3bbz1ny3cb04q">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-6751" data-mp="3bbfjcg9s29q6">
          <h2 id="ingredients-title" className={`lg:col-span-7 ${sectionHeading} text-ink`} data-id="element-6752" data-mp="36dx7oi4dlgeh">
            <HeadingText heading={copy.heading} data-id="element-6753" data-mp="2wp0am48ea7qj" />
          </h2>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-6754" data-mp="31o3915tvdfpj">{fill(copy.body)}</p>}
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-6755" data-mp="2mrvkjtbfhs4g">
          <ul className="border-t border-ink/15 lg:col-span-6" data-id="element-6756" data-mp="2rpx6qn45i7k5">
            {groups.map(g => {
            const isActive = g.key === active.key;
            return <li key={g.key} className="border-b border-ink/15" data-id="element-6757" data-mp="3fhufg5cb0jh1">
                  <button type="button" aria-pressed={isActive} aria-controls={panelId} onClick={() => setActiveKey(g.key)} onMouseEnter={() => setActiveKey(g.key)} className="group grid min-h-[64px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-4 text-left sm:gap-4" data-id="element-6758" data-mp="3kdjca0wxvm9n">
                    {/* min-w-0 + afbreken: een lange naam duwt het aantal anders buiten beeld op mobiel. */}
                    <span className={`min-w-0 break-words type-title transition-[color,transform] duration-200 ease-soft ${isActive ? 'translate-x-2 italic text-clay' : 'text-ink group-hover:translate-x-1'}`} data-id="element-6759" data-mp="1ro0frofuxbyr">
                      {g.name}
                    </span>
                    <span className={`whitespace-nowrap text-[13px] tabular-nums ${isActive ? 'text-sage' : 'text-muted'}`} data-id="element-6760" data-mp="18sac7wsa07ir">{plural(g.products.length, 'product', 'producten')}</span>
                  </button>
                </li>;
          })}
          </ul>

          <div id={panelId} aria-live="polite" className="lg:col-span-6" data-id="element-6761" data-mp="1dq2enkqjrc17">
            <AnimatePresence mode="wait" initial={false} data-id="element-6762" data-mp="2t7caznh4m77g">
              <motion.div key={active.key} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease
            }} className="bg-sand p-7 lg:sticky lg:top-[104px] lg:p-10" data-id="element-6763" data-mp="2t5rg1wqwgm92">
                <p className="text-[14px] text-sage" data-id="element-6764" data-mp="3tg2kw9k4e9a5">{active.name}</p>
                <p className="mt-3 type-title leading-snug text-ink" data-id="element-6765" data-mp="3tiyghr8hwlvf">{fill(active.effect)}</p>
                <ul className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-id="element-6766" data-mp="287yi8elk7a6">
                  {active.products.slice(0, SHOWN).map(p => <li key={p.id} data-id="element-6767" data-mp="b9xm135cym2o">
                      <Link to={`/webshop/${p.id}`} className="group flex min-h-[72px] items-center gap-4 py-3" data-id="element-6768" data-mp="b95zehtqnyoj">
                        <ProductImage src={p.image} alt="" width={56} className="h-16 w-14 shrink-0 bg-shell p-1" data-id="element-6769" data-mp="g41w3v9x7a7z" />
                        <span className="min-w-0 flex-1" data-id="element-6770" data-mp="q0577qmr2j72">
                          <span className="block text-[13px] text-clay" data-id="element-6771" data-mp="q1q25hcz845g">{p.brand}</span>
                          <span className="block truncate text-[15px] text-ink group-hover:underline group-hover:underline-offset-4" data-id="element-6772" data-mp="uzhta95srm8l">{p.name}</span>
                        </span>
                        <span className="flex items-center gap-2 text-[14px] tabular-nums text-muted" data-id="element-6773" data-mp="2aho26tgz9dsi">
                          {euro(p.price)}
                          <ArrowRightIcon className="h-4 w-4 text-ink transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6774" data-mp="25kxgnqmelpzp" />
                        </span>
                      </Link>
                    </li>)}
                </ul>
                {active.products.length > SHOWN && <p className="mt-4 text-[14px] text-muted" data-id="element-6775" data-mp="2ognk7i9ziufp">En nog {plural(active.products.length - SHOWN, 'product', 'producten')} in de collectie hierboven.</p>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Section>;
}